// src/pages/ia-paca/metiers/mlops-engineer.tsx
//
// Page métier — MLOps Engineer en PACA.
//
// Stratégie SEO : capter "recrutement MLOps", "MLOps engineer Marseille",
// "MLOps Sophia Antipolis", "platform engineer ML", "salaire MLOps 2026".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../../components/layout/layout';
import IaPacaHero from '../../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Cloud,
  Container,
  Lock,
  ShieldCheck,
  Activity,
  Workflow,
} from 'lucide-react';

export default function MlopsEngineerPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: 'MLOps Engineer',
    description:
      "Le MLOps Engineer construit la plateforme et les pipelines qui permettent aux Data Scientists et ML Engineers de livrer en production rapidement, en sécurité, à l'échelle. Convergence DevOps + Data + ML.",
    occupationLocation: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'City', name: 'Nice' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    estimatedSalary: [
      {
        '@type': 'MonetaryAmountDistribution',
        currency: 'EUR',
        duration: 'P1Y',
        percentile10: 45000,
        median: 78000,
        percentile90: 110000,
      },
    ],
    skills:
      'Kubernetes, Docker, Terraform, Ansible, AWS / GCP / Azure, CI/CD (GitHub Actions, GitLab CI, Argo), MLflow, Kubeflow, Argo Workflows, Triton, Ray, monitoring (Prometheus, Grafana, OpenTelemetry), GPU infra, Vault, IAM',
    educationRequirements:
      "Ingénieur en informatique, DevOps, ou Data avec spécialisation infra/cloud.",
    experienceRequirements: 'Junior+ à Senior (3-10+ ans), profils 100% juniors rares.',
  };

  return (
    <Layout
      title="Recrutement MLOps Engineer en PACA — Marseille, Sophia | recruteurs.io"
      description="Recruter un MLOps Engineer en PACA : profils Kubernetes, Terraform, MLflow, Argo Workflows, GPU infra. Cabinet de recrutement spécialisé qui parle ops ET ML. Salaire 45-110k€. Court-list en 20-30 jours."
      keywords="recrutement MLOps Engineer, MLOps Marseille, MLOps Sophia Antipolis, salaire MLOps 2026, recruter MLOps PACA, platform engineer ML, MLOps Kubernetes, MLOps freelance"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'MLOps Engineer', href: '/ia-paca/metiers/mlops-engineer' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Métier IA · PACA"
        title="Recruter un"
        highlight="MLOps Engineer"
        subtitle="Le profil que tout le monde veut, que personne ne trouve. La plateforme IA d'une boîte mature, ce n'est pas une stack qu'on télécharge — c'est un MLOps qui l'a construite, brique par brique, en pensant infra, sécurité, GPU, coûts, et qualité de vie des Data Scientists."
        badges={['Kubernetes', 'Terraform', 'MLflow', 'Argo Workflows', 'GPU Infra']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Tous les métiers IA
          </Link>
        </div>
      </div>

      {/* C'est quoi vraiment */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Le métier — sans bullshit
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            MLOps Engineer : DevOps pour la donnée et les modèles.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              Le MLOps Engineer est <strong>l'ingénieur plateforme dédié aux
              workloads IA et ML</strong>. Il fait pour vos Data Scientists et
              ML Engineers ce qu'un Platform Engineer DevOps fait pour vos
              dev backend : il leur fournit les rails, les outils, les
              pipelines pour livrer en production sans avoir à se battre
              avec Kubernetes ou Terraform.
            </p>
            <p>
              Sur une journée type : il maintient un cluster GPU sur EKS ou
              GKE, structure les pipelines CI/CD pour les modèles (train →
              eval → deploy → rollback), met en place le tracking MLflow ou
              équivalent, gère le feature store, monitore drift et coûts
              GPU, et organise les permissions/secrets pour que chaque
              équipe ait accès à ce dont elle a besoin sans tout casser.
            </p>
            <p>
              <strong>Pourquoi c'est rare et cher :</strong> le MLOps demande
              une <strong>double expertise rare</strong> — un DevOps qui
              comprend les besoins ML (data versioning, GPU scheduling,
              expérimentation reproductible), ou un ML Engineer qui sait
              vraiment faire du Kubernetes et du Terraform. La majorité des
              candidats qui se disent "MLOps" sont en réalité des Data
              Engineers qui ont touché à Airflow.
            </p>
          </div>
        </div>
      </section>

      {/* Stack technique */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Stack technique 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les briques que votre MLOps doit savoir assembler.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <StackCard
              Icon={Container}
              title="Conteneurs & Orchestration"
              tools={['Docker', 'Kubernetes (EKS / GKE / AKS)', 'Helm', 'Kustomize', 'KubeRay']}
            />
            <StackCard
              Icon={Cloud}
              title="Infra as Code"
              tools={['Terraform', 'Pulumi', 'AWS CDK', 'Ansible']}
            />
            <StackCard
              Icon={Workflow}
              title="Pipelines & Orchestration ML"
              tools={['Kubeflow Pipelines', 'Argo Workflows', 'Airflow', 'Prefect', 'Dagster', 'Metaflow']}
            />
            <StackCard
              Icon={Activity}
              title="Tracking, Registry, Serving"
              tools={['MLflow', 'Weights & Biases', 'BentoML', 'Triton Inference Server', 'Ray Serve']}
            />
            <StackCard
              Icon={Lock}
              title="Sécurité & Secrets"
              tools={['Vault', 'AWS Secrets Manager', 'IAM (cross-account)', 'OPA / Kyverno', 'cosign / SLSA']}
            />
            <StackCard
              Icon={ShieldCheck}
              title="Monitoring & FinOps"
              tools={['Prometheus + Grafana', 'OpenTelemetry', 'Evidently / Arize', 'Kubecost', 'GPU monitoring (DCGM)']}
            />
          </div>
        </div>
      </section>

      {/* Salaires MLOps */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires MLOps en PACA — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            45-110k€, le rôle IA le plus tendu côté offre.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Le MLOps n'a quasiment pas de marché junior (le rôle exige déjà
            une maturité DevOps + ML). Les profils confirmés sont chassés
            par tous les corporates de Sophia (Amadeus, IBM, Renesas) et
            les scale-ups parisiennes en remote. Premium sur expérience GPU
            à l'échelle.
          </p>
          <div className="mt-8">
            <SalaireMlopsTable />
          </div>
        </div>
      </section>

      {/* 5 questions pour démasquer */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Détecter les faux MLOps
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            5 questions techniques pour qualifier un MLOps Engineer.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Ces questions séparent en 15 minutes l'ingénieur plateforme ML
            mature du Data Engineer qui a vu passer du Kubeflow.
          </p>
          <div className="mt-8 space-y-4">
            {QUESTIONS.map((q, i) => (
              <div
                key={q.q}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900">{q.q}</h3>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Bon signe
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-emerald-900">{q.good}</p>
                      </div>
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                          <XCircle className="h-3.5 w-3.5" />
                          Drapeau rouge
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-rose-900">{q.bad}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Salaires tous métiers PACA */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Comparer avec les autres métiers IA en PACA
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Tableau des salaires IA en PACA — mars 2026.
          </h2>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous cherchez un MLOps en PACA ?"
        subtitle="Profil rare, nous chassons proactivement. Brief 30 min, court-list 3-5 candidats en 20-30 jours selon le niveau exigé (GPU, multi-cloud, scale)."
      />
    </Layout>
  );
}

function StackCard({
  Icon,
  title,
  tools,
}: {
  Icon: typeof Container;
  title: string;
  tools: string[];
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-violet-600" />
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {tools.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function SalaireMlopsTable() {
  const rows = [
    { ville: 'Marseille', junior: '45 – 55 k€', confirme: '55 – 78 k€', senior: '78 – 105 k€' },
    { ville: 'Aix-en-Provence', junior: '42 – 52 k€', confirme: '54 – 75 k€', senior: '75 – 100 k€' },
    { ville: 'Sophia Antipolis', junior: '48 – 58 k€', confirme: '60 – 85 k€', senior: '85 – 115 k€' },
    { ville: 'Nice', junior: '45 – 55 k€', confirme: '55 – 78 k€', senior: '78 – 105 k€' },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">Ville</th>
              <th className="px-4 py-3">Junior (rare)</th>
              <th className="px-4 py-3">Confirmé</th>
              <th className="px-4 py-3">Senior / Lead</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.ville} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-semibold text-slate-900">{r.ville}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.junior}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.confirme}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.senior}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const QUESTIONS = [
  {
    q: "Tu dois servir un modèle PyTorch sur GPU à 200 req/s. Comment tu structures l'inférence ?",
    good: "Pense Triton Inference Server ou BentoML avec dynamic batching, choix instance GPU (T4 vs A10 vs L4 selon modèle), exposition via gRPC pour latence, HPA basé sur la GPU utilization (DCGM), pré-warming. Sait expliquer le trade-off latence/throughput.",
    bad: "'Je le déploie en Flask avec Gunicorn'. Aucune notion de batching, ne sait pas dimensionner un node GPU, pense que K8s scale-up gère tout magiquement.",
  },
  {
    q: "Comment tu gères le versioning des datasets dans tes pipelines de training ?",
    good: "Cite DVC, LakeFS, Pachyderm, ou un système maison basé sur des hash + S3 versioning. Sait expliquer pourquoi Git ne marche pas pour les datasets, parle de reproductibilité bit-à-bit et de coût stockage.",
    bad: "'On stocke les CSV sur S3'. Pas de hash, pas de versioning, pas de lignage. Risque de retraining non reproductible.",
  },
  {
    q: "Vault est en panne dans ton cluster K8s. Tes pods ML ne peuvent plus récupérer leurs credentials S3. Comment tu débugges et tu réponds ?",
    good: "Pense d'abord à l'urgence (impact prod, rollback ?), regarde les logs Vault + pod, check les service accounts, IRSA / Workload Identity en fallback. Connaît la différence entre static secrets et dynamic secrets, sait expliquer le rotation et le lease.",
    bad: "'Je hardcode les credentials en attendant'. Aucune notion IRSA, ne sait pas debugger un Vault, met en risque la sécu pour aller vite.",
  },
  {
    q: "Tes Data Scientists se plaignent que leurs jobs Argo prennent 4h alors qu'ils duraient 1h il y a 3 mois. Tu fais quoi ?",
    good: "Méthodique : check resources requests/limits, GPU contention, file d'attente, queue depth, scheduler, taille des datasets en input, network throughput vers le storage. Profile un run, regarde Prometheus / Grafana, isole la cause. Peut aussi être un bug du pipeline qui charge tout en mémoire.",
    bad: "'On augmente la taille des nodes'. Pas de profiling, pas de mesure, throw money at the problem. Symptomatique d'un MLOps qui ne sait pas instrumenter son cluster.",
  },
  {
    q: "Quelle stratégie tu mets en place pour le rollout d'un nouveau modèle en prod ?",
    good: "Cite shadow deployment, canary, A/B testing avec routing par feature flag (LaunchDarkly, GrowthBook), comparaison métriques business + métriques modèle, rollback automatique sur dérive. Connaît Argo Rollouts ou Flagger.",
    bad: "'On déploie en blue/green'. Bonne base mais ne mentionne ni shadow, ni A/B sur les prédictions, ni rollback automatique sur dérive métrique.",
  },
];
